// src/pages/ConnectAccount.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { accountsAPI, API_ORIGIN } from '../utils/api';
import { useStore } from '../store';

const PLATFORMS = [
  {
    id: 'facebook', name: 'Facebook', color: '#1877F2', emoji: '📘',
    options: ['Connect Page'],
  },
  {
    id: 'instagram', name: 'Instagram', color: '#E1306C', emoji: '📸',
    options: ['Connect Personal', 'Connect Business or Creator'],
  },
  {
    id: 'gmb', name: 'Google Business Profile', color: '#1D9E75', emoji: '📍',
    options: ['Connect Profile'],
    authNote: 'Sign in with your Google account',
  },
  {
    id: 'linkedin', name: 'LinkedIn', color: '#0A66C2', emoji: '💼',
    options: ['Connect Profile', 'Connect Page'],
  },
  {
    id: 'youtube', name: 'YouTube', color: '#FF0000', emoji: '▶️',
    options: ['Connect Channel'],
    authNote: 'Sign in with your Google / Gmail account',
  },
  {
    id: 'tiktok', name: 'TikTok', color: '#000', emoji: '🎵',
    options: ['Connect Business'],
  },
  {
    id: 'x', name: 'X', color: '#000', emoji: '✖',
    options: ['Connect Profile'],
  },
  {
    id: 'pinterest', name: 'Pinterest', color: '#E60023', emoji: '📌',
    options: ['Connect Board'],
  },
];

// Platforms with full OAuth callback implemented in the backend
const OAUTH_ENABLED = new Set(['x', 'youtube', 'gmb', 'facebook', 'linkedin', 'instagram']);

export default function ConnectAccount() {
  const navigate = useNavigate();
  const { fetchAccounts, user } = useStore();
  const canManage = ['owner', 'admin'].includes(user?.org_role);
  const popupRef = useRef(null);
  const listenerRef = useRef(null);

  // LinkedIn page selection modal state
  const [liSession, setLiSession]         = useState(null); // { sessionId, profile, pages }
  const [liSelected, setLiSelected]       = useState([]);   // selected IDs
  const [liSearch, setLiSearch]           = useState('');
  const [liConnecting, setLiConnecting]   = useState(false);
  const [liTab, setLiTab]                 = useState('select'); // 'select' | 'manual'
  const [liManualName, setLiManualName]   = useState('');
  const [liManualUrl, setLiManualUrl]     = useState('');

  // Clean up message listener when component unmounts
  useEffect(() => {
    return () => {
      if (listenerRef.current) window.removeEventListener('message', listenerRef.current);
    };
  }, []);

  // The popup only knows the session id — the real profile and page list live in
  // the backend session, so fetch them rather than trusting the broadcast payload.
  const loadLinkedInSession = async (sessionId, fallbackName) => {
    let profile = { name: fallbackName || 'LinkedIn', avatar: null };
    let pages   = [];
    try {
      const { data } = await accountsAPI.linkedInSession(sessionId);
      profile = data.profile || profile;
      pages   = data.pages || [];
    } catch {
      toast.error('Could not load your LinkedIn pages. Try connecting again.');
    }

    // Only profile-mode sessions have a member id. In page mode there is nothing
    // to save a "personal" row against, so offering it just fails silently.
    const allItems = [
      ...(profile.sub ? [{ id: 'personal', name: profile.name, logo: profile.avatar, isPersonal: true }] : []),
      ...pages,
    ];
    setLiSession({ sessionId, profile, pages: allItems });
    setLiSelected(allItems.map(p => p.id));
    setLiSearch('');
    setLiTab('select');
  };

  // BroadcastChannel listener — receives LinkedIn pages even when window.opener is severed by COOP
  useEffect(() => {
    let channel;
    try {
      channel = new BroadcastChannel('postflow_oauth');
      channel.onmessage = (e) => {
        if (e.data?.type === 'oauth_linkedin_pages') {
          loadLinkedInSession(e.data.sessionId, e.data.profile?.name);
        }
      };
    } catch (e) { /* BroadcastChannel not supported */ }
    return () => { try { channel?.close(); } catch(e) {} };
  }, []);

  const openOAuthPopup = (url, platform) => {
    // Remove any existing listener
    if (listenerRef.current) window.removeEventListener('message', listenerRef.current);

    const popup = window.open(url, 'pf_oauth', 'width=620,height=720,scrollbars=yes,resizable=yes');
    popupRef.current = popup;

    if (!popup) {
      toast.error('Popup was blocked. Please allow popups for this site and try again.');
      return;
    }

    const handler = async (event) => {
      // Only trust results from our own API (the OAuth callback page) or this app
      // (the LinkedIn close page); any other window could fake an oauth_success.
      if (event.origin !== API_ORIGIN && event.origin !== window.location.origin) return;
      if (!event.data?.type?.startsWith('oauth_')) return;

      window.removeEventListener('message', handler);
      listenerRef.current = null;
      popup?.close();

      if (event.data.type === 'oauth_linkedin_pages') {
        // Show page selection modal — pages come from the backend session
        loadLinkedInSession(event.data.sessionId, event.data.profile?.name);
        return;
      }

      if (event.data.type === 'oauth_success') {
        await fetchAccounts();
        toast.success(`${event.data.name} connected successfully!`);
        navigate('/accounts');
      } else {
        toast.error(event.data.error || 'Connection failed. Please try again.');
      }
    };

    listenerRef.current = handler;
    window.addEventListener('message', handler);

    // Detect popup closed without completing OAuth
    const pollClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollClosed);
        if (listenerRef.current) {
          window.removeEventListener('message', listenerRef.current);
          listenerRef.current = null;
        }
      }
    }, 600);
  };

  const handleConnect = async (platform, option) => {
    if (OAUTH_ENABLED.has(platform)) {
      try {
        // LinkedIn "Connect Page" uses the Community Management app (mode=page);
        // everything else / "Connect Profile" uses the default OpenID app.
        const mode = platform === 'linkedin' && /page/i.test(option || '') ? 'page' : undefined;
        const res = await accountsAPI.getOAuthUrl(platform, mode);
        if (res.data.oauth_url) {
          openOAuthPopup(res.data.oauth_url, platform);
        }
      } catch (err) {
        const msg = err.response?.data?.error;
        if (msg?.includes('not supported')) {
          fallbackManual(platform);
        } else {
          toast.error(msg || 'Failed to start OAuth. Check your app credentials.');
        }
      }
    } else {
      fallbackManual(platform);
    }
  };

  const fallbackManual = async (platform) => {
    const name = prompt(`Enter account name for ${platform}:`);
    if (!name) return;
    try {
      await accountsAPI.create({
        platform,
        account_name: name,
        account_handle: `@${name.toLowerCase().replace(/\s/g, '')}`,
      });
      await fetchAccounts();
      toast.success(`${name} connected!`);
      navigate('/accounts');
    } catch (err) {
      toast.error(err.response?.status === 404 ? `${platform} isn't available yet` : 'Failed to add account');
    }
  };

  const handleLinkedInConnect = async () => {
    setLiConnecting(true);
    try {
      if (liTab === 'manual') {
        // Extract org ID from URL like https://www.linkedin.com/company/12345/ or just a number
        const raw = liManualUrl.trim();
        const match = raw.match(/\/company\/(\d+)/);
        const orgId = match ? match[1] : raw.replace(/\D/g, '');
        if (!orgId) { toast.error('Enter a valid LinkedIn Page URL or numeric ID'); setLiConnecting(false); return; }
        if (!liManualName.trim()) { toast.error('Enter the page name'); setLiConnecting(false); return; }
        await accountsAPI.post('/linkedin/connect-pages', {
          session_id:   liSession.sessionId,
          selected_ids: [`org:${orgId}`],
          manual_pages: [{ id: `org:${orgId}`, name: liManualName.trim(), logo: null }],
        });
      } else {
        if (!liSelected.length) { toast.error('Select at least one account'); setLiConnecting(false); return; }
        await accountsAPI.post('/linkedin/connect-pages', {
          session_id:   liSession.sessionId,
          selected_ids: liSelected,
        });
      }
      await fetchAccounts();
      toast.success('LinkedIn account(s) connected!');
      setLiSession(null);
      navigate('/accounts');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to connect accounts');
    } finally {
      setLiConnecting(false);
    }
  };

  const liFiltered = (liSession?.pages || []).filter(p =>
    !liSearch || p.name.toLowerCase().includes(liSearch.toLowerCase())
  );
  const liAllSelected = liFiltered.length > 0 && liFiltered.every(p => liSelected.includes(p.id));

  const toggleLiPage = (id) =>
    setLiSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleLiAll = () =>
    setLiSelected(liAllSelected ? [] : liFiltered.map(p => p.id));

  if (!canManage) {
    return (
      <div className="empty-state">
        <span style={{ fontSize: 40 }}>🔒</span>
        <h3>Only owners and admins can connect accounts</h3>
        <p>Ask an owner or admin of {user?.organization_name || 'your organization'} to connect it. You can still post to every account they connect.</p>
        <button className="btn btn-outline" onClick={() => navigate('/accounts')}>Back to accounts</button>
      </div>
    );
  }

  return (
    <div style={{ padding: 18 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#0f766e', cursor: 'pointer', marginBottom: 14 }}
        onClick={() => navigate('/accounts')}
      >
        ← Accounts
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: '#1a1f2e', borderBottom: '2px solid #f4a118', paddingBottom: 4 }}>
          Connect Account
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={{ cursor: 'pointer', color: '#8892a4', fontSize: 15 }}>❓</span>
          <button className="btn btn-outline" onClick={() => navigate('/accounts')}>Cancel</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
        {PLATFORMS.map(p => (
          <div key={p.id} className="card" style={{ padding: '18px 14px', textAlign: 'center' }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', fontSize: 24, color: '#fff' }}>
              {p.emoji}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1f2e', marginBottom: 6 }}>{p.name}</div>

            {/* Google account note for YouTube / GMB */}
            {p.authNote && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, fontSize: 10, color: '#5a6578', marginBottom: 6, background: '#f0fdfa', borderRadius: 4, padding: '3px 6px' }}>
                <span>🔑</span> {p.authNote}
              </div>
            )}

            <div style={{ height: 1, background: '#f0f1f5', marginBottom: 10 }} />

            {p.options.map(opt => (
              <div
                key={opt}
                style={{ fontSize: 12, color: '#0f766e', cursor: 'pointer', padding: '4px 0', fontWeight: 500 }}
                onClick={() => handleConnect(p.id, opt)}
              >
                {opt}
              </div>
            ))}

            {OAUTH_ENABLED.has(p.id) && (
              <div style={{ marginTop: 6, fontSize: 10, color: '#22c55e', fontWeight: 600 }}>✓ OAuth enabled</div>
            )}

            {(p.id === 'x' || p.id === 'bluesky') && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 11, color: '#555', marginTop: 5 }}>
                <input type="checkbox" defaultChecked style={{ accentColor: '#0f766e' }} /> Follow Us
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Popup blocked warning hint */}
      <div style={{ marginTop: 18, fontSize: 11, color: '#8892a4', textAlign: 'center' }}>
        If the authorization window doesn't open, allow popups for this site in your browser settings.
      </div>

      {/* LinkedIn Page Selection Modal */}
      {liSession && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, width: 540, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.18)' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid #e8eaf0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  style={{ fontSize: 12, color: '#0A66C2', cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => setLiSession(null)}
                >
                  ← Accounts
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1f2e', borderBottom: '2px solid #f4a118', paddingBottom: 2 }}>
                  Connect LinkedIn Page
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: '#8892a4', cursor: 'pointer' }}>❓</span>
                <button className="btn btn-outline" style={{ fontSize: 11, padding: '5px 12px' }} onClick={() => setLiSession(null)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 11, padding: '5px 14px' }}
                  onClick={handleLinkedInConnect}
                  disabled={liConnecting || !liSelected.length}
                >
                  {liConnecting ? 'Connecting…' : 'Connect Account'}
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e8eaf0', padding: '0 18px' }}>
              {[['select', 'Select Profile / Page'], ['manual', 'Add Business Page']].map(([id, label]) => (
                <div key={id} onClick={() => setLiTab(id)} style={{ padding: '10px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: liTab === id ? '#0A66C2' : '#8892a4', borderBottom: `2px solid ${liTab === id ? '#0A66C2' : 'transparent'}` }}>
                  {label}
                </div>
              ))}
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>

              {liTab === 'select' && <>
                {/* Select all row + search */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', color: '#1a1f2e', fontWeight: 500 }}>
                    <input type="checkbox" style={{ accentColor: '#0A66C2', width: 15, height: 15 }} checked={liAllSelected} onChange={toggleLiAll} />
                    {liSelected.length > 0
                      ? <span style={{ color: '#0A66C2' }}>{liSelected.length} selected <span style={{ color: '#e55b2d', cursor: 'pointer', fontWeight: 400 }} onClick={() => setLiSelected([])}>Clear All</span></span>
                      : 'Select All'}
                  </label>
                  <div style={{ flex: 1 }} />
                  <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e2e5ed', borderRadius: 6, padding: '5px 10px', gap: 6 }}>
                    <span style={{ color: '#aaa', fontSize: 13 }}>🔍</span>
                    <input placeholder="Search" value={liSearch} onChange={e => setLiSearch(e.target.value)} style={{ border: 'none', outline: 'none', fontSize: 12, width: 120 }} />
                  </div>
                </div>

                {liFiltered.map(page => (
                  <label key={page.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 7, cursor: 'pointer', marginBottom: 4, background: liSelected.includes(page.id) ? '#f0fdfa' : '#fafbfd', border: `1px solid ${liSelected.includes(page.id) ? '#99f6e4' : '#e8eaf0'}` }}>
                    <input type="checkbox" style={{ accentColor: '#0A66C2', width: 15, height: 15, flexShrink: 0 }} checked={liSelected.includes(page.id)} onChange={() => toggleLiPage(page.id)} />
                    {page.logo
                      ? <img src={page.logo} alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }} />
                      : <div style={{ width: 28, height: 28, borderRadius: 4, background: '#0A66C2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{page.isPersonal ? '👤' : 'in'}</div>
                    }
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1f2e' }}>{page.name}</div>
                      <div style={{ fontSize: 10, color: '#8892a4' }}>{page.isPersonal ? 'Personal Profile' : 'LinkedIn Page'}</div>
                    </div>
                  </label>
                ))}
              </>}

              {liTab === 'manual' && (
                <div>
                  <div style={{ fontSize: 12, color: '#5a6578', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '10px 12px', marginBottom: 16 }}>
                    Pages you administer are listed automatically under "Select Profile / Page".<br />
                    Use this tab only if a page is missing there — find the Page ID in its admin URL:<br />
                    <code style={{ fontSize: 11 }}>linkedin.com/company/<strong>12345678</strong>/admin/</code>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Page Name <span className="req">*</span></label>
                    <input className="form-input" placeholder="e.g. Acme Corp" value={liManualName} onChange={e => setLiManualName(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">LinkedIn Page URL or Numeric ID <span className="req">*</span></label>
                    <input className="form-input" placeholder="https://www.linkedin.com/company/12345678/ or just 12345678" value={liManualUrl} onChange={e => setLiManualUrl(e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
